import { differenceInDays, differenceInMonths, differenceInYears, parseISO, subWeeks } from 'date-fns';
import type { Age } from '../types/child';

/**
 * Calcula idade cronológica em anos, meses e dias
 */
export function calculateAge(birthDate: string | Date, referenceDate: Date = new Date()): Age {
  const birth = typeof birthDate === 'string' ? parseISO(birthDate) : birthDate;

  const years = differenceInYears(referenceDate, birth);
  const monthsTotal = differenceInMonths(referenceDate, birth);
  const months = monthsTotal % 12;

  // Calcula dias restantes após anos e meses
  const dateAfterYearsAndMonths = new Date(birth);
  dateAfterYearsAndMonths.setFullYear(birth.getFullYear() + years);
  dateAfterYearsAndMonths.setMonth(birth.getMonth() + months);
  const days = differenceInDays(referenceDate, dateAfterYearsAndMonths);

  return { years, months, days };
}

/**
 * Calcula idade corrigida para bebês prematuros
 * Subtrai as semanas de prematuridade da data de nascimento
 *
 * @param birthDate - Data de nascimento
 * @param gestationalWeeks - Semanas de gestação ao nascer (24-36)
 * @param referenceDate - Data de referência para cálculo (padrão: hoje)
 */
export function calculateCorrectedAge(
  birthDate: string | Date,
  gestationalWeeks: number,
  referenceDate: Date = new Date()
): Age {
  if (gestationalWeeks < 24 || gestationalWeeks > 36) {
    throw new Error('Gestational weeks must be between 24 and 36');
  }

  const birth = typeof birthDate === 'string' ? parseISO(birthDate) : birthDate;
  const fullTermWeeks = 40;
  const weeksEarly = fullTermWeeks - gestationalWeeks;

  // Subtrai as semanas de prematuridade da data de nascimento
  // para obter a "data de nascimento corrigida"
  const correctedBirthDate = subWeeks(birth, weeksEarly);

  return calculateAge(correctedBirthDate, referenceDate);
}

/**
 * Converte uma idade (Age) para meses decimais
 */
export function ageToMonths(age: Age): number {
  // Aproximação: 1 mês = 30.44 dias (365.25 / 12)
  const monthsFromDays = age.days / 30.44;
  return age.years * 12 + age.months + monthsFromDays;
}

/**
 * Calcula idade em meses decimais diretamente
 */
export function calculateAgeInMonths(birthDate: string | Date, referenceDate: Date = new Date()): number {
  const age = calculateAge(birthDate, referenceDate);
  return ageToMonths(age);
}

/**
 * Calcula idade corrigida em meses decimais para prematuros
 */
export function calculateCorrectedAgeInMonths(
  birthDate: string | Date,
  gestationalWeeks: number,
  referenceDate: Date = new Date()
): number {
  const age = calculateCorrectedAge(birthDate, gestationalWeeks, referenceDate);
  return ageToMonths(age);
}

/**
 * Formata idade para exibição (ex: "2 anos, 3 meses, 5 dias")
 */
export function formatAge(age: Age): string {
  const parts: string[] = [];

  if (age.years > 0) {
    parts.push(`${age.years} ${age.years === 1 ? 'ano' : 'anos'}`);
  }

  if (age.months > 0) {
    parts.push(`${age.months} ${age.months === 1 ? 'mês' : 'meses'}`);
  }

  if (age.days > 0 || parts.length === 0) {
    parts.push(`${age.days} ${age.days === 1 ? 'dia' : 'dias'}`);
  }

  return parts.join(', ');
}
